import { Table, Row, Modal, Input, Tag, DatePicker } from 'antd';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import classNames from 'classnames';
import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import dayjs from 'dayjs';
import debouce from 'lodash/debounce';
import {
    FOLIOS_MODE,
    InvoiceStatusColorMapping,
    InvoiceStatusMapping,
    TInvoice,
    TViolation,
} from './type';
import { LineItemsFolios } from './line-items';
import { useSortData } from '../../integrated_folios';
import { ICNoneData, ICSearch, ICSort } from '../../../icons';
import { IPagination, SearchQueryParams } from '../../../interface/paging';
import { useNotification } from '../../../hooks/useNotification';
import { removeUndefinedAttribute } from '../../../utils';
import { setLoading } from '../../../redux/slices/appInfo';
import { EInvoiceTooltip } from '../../../components/tooltip';
import { ICMaterialSymbol } from '../../../icons/material-symbol';
import { EInvoiceButton, IconButton } from '../../../components/button';
import { EInvoicePagination } from '../../../components/pagination';
import { submitInvoice } from '../../../api/invoice';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { ExportActions } from './actions';
import { EcoScrollTable } from '../scroll-table';
import { SOURCE_PAGE } from '../../../utils/enums';

const ActionMenu = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    cursor: pointer;
    padding-right: 16px;
    &:hover {
        background-color: #f7f8fa;
    }
    button {
        padding: 0 !important;
    }
`;

const WrapperTable = styled.div`
    .ant-table-thead > tr > th {
        background-color: white;
    }
    .data-row:nth-child(odd) {
        background-color: #f7f8fa;
    }
    .ant-table-column-sorters .ant-table-column-sorter-inner {
        .anticon {
            display: none;
        }
    }

    .ant-table-expanded-row.ant-table-expanded-row-level-1 {
        td.ant-table-cell:first-child {
            padding: 0 !important;
        }
    }
    .ant-table-tbody .ant-table-row.ant-table-row-selected > .ant-table-cell {
        background-color: white;
    }

    .ant-picker {
        width: 100%;
    }

    .ant-picker-range {
        display: flex;
        justify-content: space-between;
    }

    .ant-picker-panel-container {
        display: flex;
        flex-direction: row;
    }
`;

const WrapperInput = styled.div`
    @media (max-width: 768px) {
    .ant-input-group-wrapper {
        width: 100% !important;
    }
`;

const WrapperButton = styled.div`
    @media (max-width: 768px) {
        width: 100%;
        .ant-btn {
            width: 100% !important;
        }
    }
`;

type SortableColumnProps = {
    title: string;
    order?: SortOrder;
    name: string;
};
const SortableColumn: React.FC<SortableColumnProps> = ({
    title,
    order,
    name,
}) => {
    const { setSortData } = useSortData();
    return (
        <div
            className="flex space-x-3 items-center justify-between select-none group"
            onClick={() => {
                setSortData({
                    field: order === 'descend' ? 'createdDate' : name,
                    order:
                        order === 'descend' ? 'desc' : !order ? 'asc' : 'desc',
                });
            }}
        >
            <span className="text-body-bold text-high-em">
                {title as string}
            </span>
            <ICSort order={order} />
        </div>
    );
};

type FoliosPageListProps = {
    source: SOURCE_PAGE;
    foliosMode: FOLIOS_MODE;
    invoiceList: TInvoice[];
    keyword?: string;
    pagination?: IPagination;
    onChangePaging: ({ page }: { page: number }) => void;
    onReload: () => void;
    filterParams: SearchQueryParams;
    onFilterData: (payload: SearchQueryParams) => void;
    selectedRowKeys: React.Key[];
    setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
};
const KeywordContext = React.createContext<string>('');
const { RangePicker } = DatePicker;

export const FoliosPageList: React.FC<FoliosPageListProps> = ({
    source,
    foliosMode,
    invoiceList,
    keyword,
    pagination,
    onChangePaging,
    onReload,
    filterParams,
    onFilterData,
    selectedRowKeys,
    setSelectedRowKeys,
}) => {
    const size = useScreenSize();
    const mobileMode = size === 'xs' || size === 'sm';
    const dispatch = useDispatch();
    const { showSuccess, showError } = useNotification();
    const onChangePage = (page: number) => {
        onChangePaging({ page });
    };
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [foliosId, setFoliosId] = React.useState<string | undefined>('');
    const [foliosSelected, setFoliosSelected] = React.useState<
        TInvoice | undefined
    >(undefined);
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);
    const [openLineItems, setOpenLineItems] = React.useState<boolean>(false);

    // handle search keyword
    const [searchWord, setSearchWord] = React.useState<string | undefined>('');
    const [showErrorSubmit, setShowErrorSubmit] = React.useState(false);
    const [violations, setViolations] = React.useState<TViolation[]>([]);

    // const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>(
    //     []
    // );
    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const onSearchData = React.useCallback(
        async (searchWord: string | undefined) => {
            try {
                const queryParams: SearchQueryParams = {
                    ...filterParams,
                    search: searchWord,
                };
                const newPayload = removeUndefinedAttribute(queryParams);
                onFilterData(newPayload);
            } catch (error) {
                // Handle error
            }
        },
        [filterParams]
    );

    const handleSearchData = (value: string | undefined) => {
        onSearchKeyword(value);
        setSearchWord(value);
    };

    const onSearchKeyword = React.useMemo(() => {
        return debouce(onSearchData, 600);
    }, [onSearchData, filterParams]);

    React.useEffect(() => {
        return () => {
            onSearchKeyword.cancel();
        };
    }, [onSearchKeyword]);
    // end handle search keyword

    const onShowDeleteModal = React.useCallback(
        (id: string) => {
            setFoliosId(id);
            setShowDeleteModal(true);
        },
        [setShowDeleteModal]
    );

    const onSubmitInvoice = React.useCallback(
        async (id: string) => {
            try {
                dispatch(setLoading(true));
                const result = await submitInvoice(id);
                if (result.status === 200) {
                    if (
                        result.data?.success === false &&
                        result.data?.violations?.length
                    ) {
                        setViolations(result.data.violations);
                        setShowErrorSubmit(true);
                    } else {
                        showSuccess('Success', 'Submit invoice successfully');
                        onReload();
                    }
                }
            } catch (error: any) {
                showError('Error', error?.message);
            } finally {
                dispatch(setLoading(false));
            }
        },
        [dispatch, showError, showSuccess, onReload]
    );

    const columns: ColumnsType<TInvoice> = React.useMemo(() => {
        const lineItemsColumn = {
            title: 'Transaction',
            dataIndex: 'count_line_items',
            key: 'count_line_items',
            sorter: false,
            align: 'center' as const,
            render: (value, record: TInvoice) => {
                return (
                    <EInvoiceTooltip
                        content="View Transaction"
                        placement="top"
                        childNode={
                            <div
                                onClick={() => {
                                    setFoliosSelected(record);
                                    setOpenLineItems(true);
                                }}
                                className="flex cursor-pointer justify-center items-center underline text-center text-blue"
                            >
                                {value}
                            </div>
                        }
                    />
                );
            },
        };
        const actionsColumn = {
            title: 'Action',
            dataIndex: 'invoiceNo',
            key: 'invoiceNo',
            width: '40px',
            render: (invoiceNo: string, data: TInvoice) => {
                return (
                    <ActionMenu>
                        <EInvoiceTooltip
                            content="Submit"
                            placement="top"
                            childNode={
                                <IconButton
                                    icon={<ICMaterialSymbol icon="sync" />}
                                    btnType="tertiary"
                                    onClick={event => {
                                        event.stopPropagation();
                                        setFoliosSelected(data);
                                        setShowConfirmModal(true);
                                    }}
                                />
                            }
                        />
                        <EInvoiceTooltip
                            content="Delete"
                            placement="top"
                            childNode={
                                <IconButton
                                    icon={<ICMaterialSymbol icon="delete" />}
                                    onClick={event => {
                                        event.stopPropagation();
                                        onShowDeleteModal(invoiceNo);
                                    }}
                                    btnType="tertiary"
                                />
                            }
                        />
                    </ActionMenu>
                );
            },
        };
        const defaultColumns = [
            {
                title: 'Folio No.',
                dataIndex: 'invoice_no',
                field: 'invoice_no',
                key: 'invoice_no',
                sorter: false,
            },
            {
                title: 'Created Date',
                dataIndex: 'created_on',
                field: 'created_on',
                key: 'created_on',
                sorter: false,
                render: (val: string) => {
                    const newDate = dayjs(val, 'YYYY-MM-DD').format(
                        'YYYY-MM-DD'
                    );
                    return newDate;
                },
            },
            {
                title: 'Status',
                dataIndex: 'status',
                field: 'status',
                key: 'status',
                sorter: false,
                render: (status: string) => {
                    return (
                        <div>
                            <Tag color={InvoiceStatusColorMapping[status]}>
                                {InvoiceStatusMapping[status]}
                            </Tag>
                        </div>
                    );
                },
            },
            {
                title: 'Buyer Name',
                dataIndex: 'buyer_name',
                field: 'buyer_name',
                key: 'buyer_name',
                sorter: false,
            },
            {
                title: 'Buyer Tin',
                dataIndex: 'buyer_tin',
                field: 'buyer_tin',
                key: 'buyer_tin',
                sorter: false,
            },
            {
                title: 'Currency Code',
                dataIndex: 'currency_code',
                field: 'currency_code',
                key: 'currency_code',
                sorter: false,
            },
            {
                title: 'Total Exclude Tax',
                dataIndex: 'total_exclude_tax',
                field: 'total_exclude_tax',
                key: 'total_exclude_tax',
                sorter: false,
                align: 'right' as const,
            },
            {
                title: 'Total Include Tax',
                dataIndex: 'total_include_tax',
                field: 'total_include_tax',
                key: 'total_include_tax',
                sorter: false,
                align: 'right' as const,
            },
            {
                title: 'Total Payable',
                dataIndex: 'total_payable',
                field: 'total_payable',
                key: 'total_payable',
                sorter: false,
                align: 'right' as const,
            },
            {
                title: 'Total Tax',
                dataIndex: 'total_tax',
                field: 'total_tax',
                key: 'total_tax',
                sorter: false,
                align: 'right' as const,
            },
            {
                title: 'Total Tax Per Tax Type',
                dataIndex: 'total_tax_per_tax_type',
                field: 'total_tax_per_tax_type',
                key: 'total_tax_per_tax_type',
                sorter: false,
                align: 'right' as const,
            },
        ].map((col: ColumnType<TInvoice>) => {
            if (col.sorter) {
                const oldTitle = col.title;
                col.title = ({ sortColumns }) => {
                    const sortedColumn = sortColumns?.find(
                        ({ column }) => column.key === col.key
                    );
                    return (
                        <SortableColumn
                            title={oldTitle as string}
                            order={sortedColumn?.order}
                            name={col.dataIndex as string}
                        />
                    );
                };
            } else {
                col.title = (
                    <div className="text-body-bold text-high-em">
                        {col.title as string}
                    </div>
                );
            }
            col.showSorterTooltip = false;
            return col;
        }) as ColumnsType<TInvoice>;

        if (foliosMode !== FOLIOS_MODE.SUBMITTED) {
            defaultColumns.splice(0, 0, lineItemsColumn);
            defaultColumns.splice(1, 0, actionsColumn);
        }
        return defaultColumns;
    }, [foliosMode]);

    const onDateChange = React.useCallback(
        (dates: any, dateStrings: [string, string]) => {
            const startDate = dateStrings[0];
            const endDate = dateStrings[1];
            const filterMode =
                foliosMode === FOLIOS_MODE.INTEGRATED
                    ? 'status:neq:SUBMITTED'
                    : 'status:eq:SUBMITTED';
            let newPayload = filterParams;
            if (!startDate || !endDate) {
                delete newPayload.created_on;
                newPayload = {
                    ...filterParams,
                    filter: filterMode,
                };
            } else {
                const startDateTimestamp = dayjs(startDate).unix();
                const endDateTimestamp = dayjs(endDate).unix();
                const newFilter = `created_on:gt:${startDateTimestamp},created_on:lt:${endDateTimestamp}`;
                newPayload = {
                    ...filterParams,
                    filter: `${filterMode},${newFilter}`,
                };
            }
            onFilterData(newPayload);
        },
        [filterParams, foliosMode]
    );

    const onSubmitSearch = React.useCallback(() => {
        const newPayload = {
            ...filterParams,
            search: searchWord,
        };
        onFilterData(newPayload);
    }, [filterParams, searchWord]);

    return (
        <WrapperTable className="flex flex-col gap-[20px] w-full overflow-hidden h-full items-start box-border">
            <div className="grid grid-cols-[1fr_300px] w-full px-[12px] py-[20px] shadow-e-03 max-md:justify-center items-center max-md:flex-wrap gap-3 justify-between bg-white rounded-[12px]">
                <div className="grid grid-cols-[1fr_300px] gap-3 items-center justify-start w-full flex-wrap">
                    <WrapperInput className="flex items-center gap-2">
                        <Input
                            placeholder="Search by ..."
                            value={searchWord}
                            onChange={e => handleSearchData(e.target.value)}
                            style={{
                                width: '100%',
                            }}
                            size="large"
                        />
                    </WrapperInput>
                    <div className="flex w-full">
                        <RangePicker
                            format="YYYY-MM-DD"
                            datatype="date"
                            size="large"
                            style={{ width: '100%' }}
                            onChange={(dates, dateStrings) => {
                                onDateChange(dates, dateStrings);
                            }}
                        />
                    </div>
                </div>
                <WrapperButton className="flex gap-[10px]">
                    <EInvoiceButton
                        btnType="primary"
                        style={{
                            width: '150px',
                            height: '40px',
                        }}
                        btnSize="md"
                        onClick={onSubmitSearch}
                    >
                        <div className="flex space-x-2 items-center">
                            <ICSearch stroke="white" width={18} height={18} />
                            <span>Search</span>
                        </div>
                    </EInvoiceButton>
                    <ExportActions
                        source={source}
                        filterParams={filterParams}
                        selectedRowKeys={selectedRowKeys}
                    />
                </WrapperButton>
            </div>
            <div
                className={classNames([
                    'flex flex-col w-full h-full rounded-[12px] overflow-hidden shadow-e-03',
                    {
                        'bg-white': !mobileMode,
                    },
                ])}
            >
                <EcoScrollTable totalPages={pagination?.totalCount} />
                <KeywordContext.Provider value={keyword ?? ''}>
                    <Table
                        key="invoiceList"
                        className={classNames('white-header w-full')}
                        columns={columns}
                        rowSelection={rowSelection}
                        dataSource={invoiceList}
                        rowClassName="data-row"
                        rowKey="invoice_no"
                        scroll={{ x: 'max-content' }}
                        locale={{
                            emptyText: <EmptyTable keyword={keyword} />,
                        }}
                        pagination={false}
                        // onRow={record => {
                        //     return {
                        //         onClick: () => {
                        //             setFoliosSelected(record);
                        //             setOpenLineItems(true);
                        //         },
                        //     };
                        // }}
                    />
                </KeywordContext.Provider>
                {pagination?.totalPage && pagination?.totalPage > 1 ? (
                    <Row
                        gutter={16}
                        style={{
                            margin: 0,
                        }}
                        className={classNames([
                            'grid items-center max-md:justify-center justify-end w-full bg-white p-[16px] border-t border-solid border-outline-med rounded-b-[12px]',
                            'max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:z-10 max-md:rounded-none',
                        ])}
                    >
                        <EInvoicePagination
                            onChange={onChangePage}
                            defaultCurrent={pagination?.page}
                            defaultPageSize={pagination?.pageSize}
                            total={pagination?.totalCount}
                            currentPage={pagination?.page}
                            showSizeChanger={false}
                        />
                    </Row>
                ) : null}
            </div>

            <Modal
                centered
                onCancel={() => setShowDeleteModal(false)}
                open={showDeleteModal}
                destroyOnClose
                title={<div className="font-bold text-[18px]">Confirm</div>}
                footer={
                    <div className="grid grid-cols-[auto_auto] justify-end gap-[16px]">
                        <EInvoiceButton
                            btnType="sub"
                            btnSize="sm"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            Cancel
                        </EInvoiceButton>
                        <EInvoiceButton btnType="danger" btnSize="sm">
                            Delete
                        </EInvoiceButton>
                    </div>
                }
            >
                <p>Are you sure you want to delete this Folios?</p>
            </Modal>

            <Modal
                centered
                onCancel={() => setShowConfirmModal(false)}
                open={showConfirmModal}
                destroyOnClose
                title={<div className="font-bold text-[18px]">Confirm</div>}
                footer={
                    <div className="grid grid-cols-[auto_auto] justify-end gap-[16px]">
                        <EInvoiceButton
                            btnType="sub"
                            btnSize="sm"
                            onClick={() => setShowConfirmModal(false)}
                        >
                            Cancel
                        </EInvoiceButton>
                        <EInvoiceButton
                            btnType="primary"
                            btnSize="sm"
                            onClick={() => {
                                if (foliosSelected) {
                                    onSubmitInvoice(foliosSelected.id);
                                }
                                setShowConfirmModal(false);
                            }}
                        >
                            Confirm
                        </EInvoiceButton>
                    </div>
                }
            >
                <p>Are you sure you want to submit this Folios?</p>
            </Modal>

            <Modal
                centered
                onCancel={() => setOpenLineItems(false)}
                open={openLineItems}
                destroyOnClose
                wrapClassName="line-items-modal"
                width={1000}
                getContainer={'root'}
                afterClose={() => setFoliosSelected(undefined)}
                title={
                    <div className="font-bold text-[18px]">
                        Transaction: {foliosSelected?.invoice_no} -{' '}
                        {foliosSelected?.buyer_name}
                    </div>
                }
                footer={
                    <div className="grid grid-cols-[auto_auto] justify-end gap-[16px]">
                        <EInvoiceButton
                            btnType="sub"
                            btnSize="sm"
                            onClick={() => setOpenLineItems(false)}
                        >
                            Close
                        </EInvoiceButton>
                    </div>
                }
            >
                <LineItemsFolios invoiceSelected={foliosSelected} />
            </Modal>

            <Modal
                centered
                onCancel={() => setShowErrorSubmit(false)}
                open={showErrorSubmit}
                destroyOnClose
                maskClosable={false}
                width={1000}
                title={
                    <div className="flex flex-col">
                        <div className="font-bold text-[18px]">
                            Submit Invoice Failed - {foliosSelected?.invoice_no}
                        </div>
                        <div className="text-sm text-gray-500">
                            {violations?.length} violations
                        </div>
                    </div>
                }
                footer={
                    <div className="grid grid-cols-[auto_auto] justify-end gap-[16px]">
                        <EInvoiceButton
                            btnType="sub_info"
                            btnSize="sm"
                            onClick={() => setShowErrorSubmit(false)}
                        >
                            Close
                        </EInvoiceButton>
                    </div>
                }
            >
                <Table
                    className={classNames('white-header w-full')}
                    rowClassName="data-row"
                    scroll={{ x: 'max-content' }}
                    columns={[
                        {
                            title: 'No',
                            dataIndex: 'no',
                            key: 'no',
                            align: 'center' as const,
                            render: (value, record, index) => index + 1,
                        },
                        {
                            title: 'Violation Code',
                            dataIndex: 'code',
                            key: 'code',
                            sorter: false,
                        },
                        {
                            title: 'Violation Message',
                            dataIndex: 'message',
                            key: 'message',
                            sorter: false,
                        },
                    ]}
                    dataSource={violations}
                    rowKey="code"
                    pagination={false}
                />
            </Modal>
        </WrapperTable>
    );
};

type IEmptyTableProps = {
    keyword: string | undefined;
};

const EmptyTable: React.FC<IEmptyTableProps> = ({ keyword }) => {
    return (
        <div className="my-6 flex flex-col gap-[5px]">
            <div className="flex justify-center">
                <ICNoneData />
            </div>
            <div className="text-standard-bold text-high-em">
                {keyword
                    ? `No data found with keyword “${keyword}”`
                    : 'No data found'}
            </div>
        </div>
    );
};
